import React, { useEffect } from 'react';
import { useEffectOnce } from 'react-use';
import { Route, Router, Switch, Redirect } from 'react-router-dom'; // Switch, Redirect
import { createBrowserHistory } from 'history';
import loadable from '@loadable/component';
import { connect } from 'react-redux';
import Main from '../Layouts/Main';
import { localeList } from '../../utils/Helper';
import store from '../../redux/store';
import { getSiteConfig } from '../../redux/modules/siteConfig/action';

import c from '../../config';

// Default Style
import '../../assets/scss/theme.scss';

// cookie
const CookieConsent = loadable(() => import('../../components/CookieConsent'));

// SSO
const SSOStatus = loadable(() => import('../../components/SSOStatus'));

// Routes
const HomePage = loadable(() => import('../../pages/HomePage'));
const IndexPage = loadable(() => import('../../pages/IndexPage'));
const StaticPage = loadable(() => import('../../pages/StaticPage'));
const SpeakersPage = loadable(() => import('../../pages/SpeakersPage'));
const ProgrammePage = loadable(() => import('../../pages/ProgrammePage'));
const PhotoGalleryPage = loadable(() => import('../../pages/PhotoGalleryPage'));
const VideoGalleryPage = loadable(() => import('../../pages/VideoGalleryPage'));
const LinksPage = loadable(() => import('../../pages/LinksPage'));
const PressReleasePage = loadable(() => import('../../pages/PressReleasePage'));
const PromotionPage = loadable(() => import('../../pages/PromotionPage'));
const NoMatch = loadable(() => import('../../pages/NoMatch'));
const ComingSoonPage = loadable(() => import('../../pages/ComingSoonPage'));

// Below two pages are cloned from the StaticPage but disable the Tealium fire action, Tealium will be fired via Custom JS script in DCR to provide extra parameters
const BrandDetailPage = loadable(() => import('../../pages/BrandDetailPage'));
const SearchPage = loadable(() => import('../../pages/SearchPage'));

// Config Top Application, include languages
const TopApp = loadable(() => import('../../widgets/TopApp'));

const history = createBrowserHistory();

const App = ({ ...props }) => {
  const {
    siteConfig,
    generalPage,
    indexPage,
    indexPageList,
    widgetArticleList,
    sbeProgramme,
    sbeSpeaker,
    announcement,
  } = props;
  const { data = {} } = siteConfig || {};
  const { availableLanguages, defaultLanguage, enableSSO, under_construction } = data;
  const isUnderConstruction = under_construction === 'true' || under_construction === true;

  useEffectOnce(() => {
    store.dispatch(getSiteConfig());
  });

  // Inform prerendering services that the SPA is ready to be snapshotted
  // once core configuration and all main page data reducers have finished loading.
  // This includes:
  // - siteConfig: Site configuration (required for routing)
  // - generalPage: Used by HomePage, StaticPage, PressReleasePage, PromotionPage, LinksPage, PhotoGalleryPage, VideoGalleryPage, SpeakersPage, ProgrammePage
  // - indexPage: Used by IndexPage
  // - indexPageList: Used by IndexPage for paginated lists
  // - widgetArticleList: Used by HomePage for article slider
  // - sbeProgramme: Used by ProgrammePage for SBE programme data
  // - sbeSpeaker: Used by SpeakersPage for SBE speaker data
  // - announcement: Used by NotificationGroup (loaded on all pages via Main layout)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const hasConfig = !!(siteConfig && siteConfig.data && siteConfig.data.availableLanguages);

    const reducers = [
      siteConfig,
      generalPage,
      indexPage,
      indexPageList,
      widgetArticleList,
      sbeProgramme,
      sbeSpeaker,
      announcement,
    ];

    const allIdle =
      reducers.length > 0 &&
      reducers.every(
        (r) => r && Object.prototype.hasOwnProperty.call(r, 'isLoading') && r.isLoading === false,
      );

    if (hasConfig && allIdle) {
      window.prerenderReady = true;
    }
  }, [
    siteConfig,
    generalPage,
    indexPage,
    indexPageList,
    widgetArticleList,
    sbeProgramme,
    sbeSpeaker,
    announcement,
  ]);

  return (
    <>
      <CookieConsent />
      {enableSSO && <SSOStatus />}
      <div id="hktdc-corporate-header"></div>
      {availableLanguages && (
        <Router history={history} basename={c.routePathTo}>
          <Route
            path={`${c.routePathTo}/:locale(${localeList(availableLanguages)})?`}
            component={TopApp}
          />
          <Main
            availableLanguages={localeList(availableLanguages)}
            isUnderConstruction={isUnderConstruction}
          >
            <Switch>
              {isUnderConstruction && <Route component={ComingSoonPage} />}
              <Route
                exact
                path={`${c.routePathTo}/:locale(${localeList(availableLanguages)})?`}
                component={HomePage}
              />
              <Route
                exact
                path={`${c.routePathTo}/:locale(${localeList(
                  availableLanguages,
                )})/photo/:pageName/:id?`}
                component={PhotoGalleryPage}
              />
              <Route
                exact
                path={`${c.routePathTo}/:locale(${localeList(
                  availableLanguages,
                )})/video/:pageName/:id?`}
                component={VideoGalleryPage}
              />
              <Route
                exact
                path={`${c.routePathTo}/:locale(${localeList(
                  availableLanguages,
                )})/speaker/:pageName/:id?`}
                component={SpeakersPage}
              />
              <Route
                exact
                path={`${c.routePathTo}/:locale(${localeList(
                  availableLanguages,
                )})/programme/:pageName`}
                component={ProgrammePage}
              />
              <Route
                exact
                path={`${c.routePathTo}/:locale(${localeList(
                  availableLanguages,
                )})/s/:pageName(sg-brand-details|ml-brand-details)`}
                component={BrandDetailPage}
              />
              <Route
                exact
                path={`${c.routePathTo}/:locale(${localeList(
                  availableLanguages,
                )})/s/:pageName(sg-search|sg-product-search|ml-search|ml-product-search)`}
                component={SearchPage}
              />
              <Route
                exact
                path={`${c.routePathTo}/:locale(${localeList(availableLanguages)})/s/:pageName`}
                component={StaticPage}
              />
              <Route
                exact
                path={`${c.routePathTo}/:locale(${localeList(
                  availableLanguages,
                )})/i/:pageName/:curPage?`}
                component={IndexPage}
              />
              <Route
                exact
                path={`${c.routePathTo}/:locale(${localeList(availableLanguages)})/l/:pageName`}
                component={LinksPage}
              />
              <Route
                exact
                path={`${c.routePathTo}/:locale(${localeList(
                  availableLanguages,
                )})/press-release/:pageName`}
                component={PressReleasePage}
              />
              <Route
                exact
                path={`${c.routePathTo}/:locale(${localeList(
                  availableLanguages,
                )})/promotion/:pageName`}
                component={PromotionPage}
              />
              <Route
                exact
                path={`${c.routePathTo}/:path`}
                render={(props) => (
                  <Redirect to={`${c.routePathTo}/${defaultLanguage}/${props.match.params.path}`} />
                )}
              />
              <Route component={NoMatch} />
            </Switch>
          </Main>
        </Router>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  siteConfig: state.siteConfig,
  generalPage: state.generalPage,
  indexPage: state.indexPage,
  indexPageList: state.indexPageList,
  widgetArticleList: state.widgetArticleList,
  sbeProgramme: state.sbeProgramme,
  sbeSpeaker: state.sbeSpeaker,
  announcement: state.announcement,
});

const connectStore = connect(mapStateToProps, null, null, {
  pure: true,
})(App);

export { App as AppComponent }; // <- For component Unit Test
export default connectStore;
